/* eslint-disable react/require-default-props */
import {
  ContractStatus,
  convertContractStatus,
} from '@/api/theHive/notices/enumerations';
import { SignedContractData } from '@/api/theHive/notices/types';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { useState } from 'react';

import {
  IconAttachFileMS,
  IconDescriptionMS,
} from '../../../../../../constants/icons';
import { formatOnlyDate } from '../../../../../../helpers';
import useGeneral from '../../../../../../hooks/useGeneral';
import {
  TableCellHeader,
  StyledCell,
  StyledRow,
  StatusCell,
  DownloadButton,
  ActionButton,
  StyledTooltip,
  ButtonsWrapper,
} from '../styles';
import { stripHtml } from '../utils';
import { RejectDialog } from './RejectDialog';
import { RejectReasonDialog } from './RejectionReason';

interface ContractTableProps {
  name: string;
  data: SignedContractData | null;
  docLoading: boolean;
  handleApprove: () => Promise<void>;
  handleReject: (reason: string) => Promise<void>;
}

export default function ContractTable({
  name,
  data,
  docLoading,
  handleApprove,
  handleReject,
}: ContractTableProps): JSX.Element {
  const [openRejectionDialog, setOpenRejectionDialog] = useState(false);

  const { handleDownloadFile } = useGeneral();

  const disbleddActions =
    data?.status === ContractStatus.APPROVED ||
    data?.status === ContractStatus.REJECTED ||
    !data?.uploaded_at;

  const closeRejectionDialog = (): void => setOpenRejectionDialog(false);

  const handleDownload = (): void => {
    if (data?.url && data?.original_filename) {
      handleDownloadFile(data.url, data.original_filename);
    }
  };

  const getDocumentStatusColor = (status: ContractStatus): string => {
    if (status === ContractStatus.APPROVED) return '#00B39B';
    if (status === ContractStatus.REJECTED) return '#F90B0B';
    return '#FF6600';
  };

  return (
    <Box my={5}>
      <Typography color="common.grey" fontSize={21} fontWeight={600} my={3}>
        {name}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader sx={{ padding: '20px 10px', width: '3%' }}>
                {IconAttachFileMS}
              </TableCellHeader>
              <TableCellHeader width="17%">documento</TableCellHeader>
              <TableCellHeader align="center" width="15%">
                data de entrada
              </TableCellHeader>
              <TableCellHeader align="center" width="15%">
                status
              </TableCellHeader>
              <TableCellHeader align="center" width="20%">
                motivo
              </TableCellHeader>
              <TableCellHeader align="center" width="30%">
                ações
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {!data ? (
              <StyledRow>
                <StyledCell align="center" colSpan={6}>
                  Documento pendente de envio
                </StyledCell>
              </StyledRow>
            ) : (
              <StyledRow>
                <StyledCell sx={{ padding: '20px 10px' }}>
                  <DownloadButton
                    disabled={!data?.url}
                    onClick={handleDownload}
                  >
                    {IconDescriptionMS}
                  </DownloadButton>
                </StyledCell>
                <StyledCell>
                  <StyledTooltip title={name} placement="top">
                    <Box>
                      <ActionButton
                        disabled={!data?.url}
                        onClick={handleDownload}
                      >
                        <span>{name}</span>
                      </ActionButton>
                    </Box>
                  </StyledTooltip>
                </StyledCell>
                <StyledCell align="center">
                  {formatOnlyDate(data?.uploaded_at)}
                </StyledCell>
                <StatusCell
                  align="center"
                  sx={{
                    color: data?.status && getDocumentStatusColor(data.status),
                  }}
                >
                  {data?.status && convertContractStatus(data.status)}
                </StatusCell>
                <StyledCell align="center">
                  <ActionButton
                    sx={{ textAlign: 'center' }}
                    onClick={() => setOpenRejectionDialog(true)}
                  >
                    <span>{stripHtml(data?.rejection_reason)}</span>
                  </ActionButton>
                </StyledCell>
                <StyledCell>
                  <ButtonsWrapper>
                    <RejectDialog
                      name={name}
                      document={data?.original_filename || ''}
                      docLoading={docLoading}
                      handleReject={handleReject}
                      disabled={disbleddActions}
                    />
                    <Button
                      disabled={docLoading || disbleddActions}
                      sx={{ maxWidth: '140px', height: '40px' }}
                      onClick={() => handleApprove()}
                    >
                      aprovar
                    </Button>
                  </ButtonsWrapper>
                </StyledCell>
              </StyledRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {openRejectionDialog && (
        <RejectReasonDialog
          open
          closeDialog={closeRejectionDialog}
          rejectionReason={data?.rejection_reason || ''}
          document={name}
        />
      )}
    </Box>
  );
}
